import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface DashboardMetric {
  key: string;
  label: string;
  detail: string;
  value: number | null;
  icon: ReactNode;
  iconClass: string;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

interface DashboardStatsProps {
  metrics: DashboardMetric[];
  locale: string;
}

export const DashboardStats = ({ metrics, locale }: DashboardStatsProps) => {
  const { t } = useTranslation();

  if (metrics.length === 0) {
    return (
      <section
        aria-label={t('admin.dashboard.stats_label')}
        className="rounded-[20px] border border-slate-200 bg-white px-6 py-8 text-center shadow-sm"
      >
        <span className="material-symbols-outlined text-4xl text-slate-300">dashboard</span>
        <h2 className="mt-3 text-base font-semibold text-slate-900">
          {t('admin.dashboard.no_metrics.title')}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          {t('admin.dashboard.no_metrics.description')}
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label={t('admin.dashboard.stats_label')}
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
    >
      {metrics.map((metric) => (
        <article
          key={metric.key}
          className="rounded-[20px] border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-500">{metric.label}</p>
              {metric.isLoading || (metric.value === null && !metric.error) ? (
                <div
                  aria-label={t('admin.dashboard.loading')}
                  className="mt-3 h-9 w-24 animate-pulse rounded-lg bg-slate-100"
                />
              ) : metric.error ? (
                <div className="mt-3" role="alert">
                  <p className="text-sm text-rose-600">{metric.error}</p>
                  <button
                    type="button"
                    onClick={metric.onRetry}
                    className="mt-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
                  >
                    {t('admin.dashboard.actions.retry')}
                  </button>
                </div>
              ) : (
                <p className="mt-2 text-3xl font-bold tracking-tight text-slate-950">
                  {metric.value?.toLocaleString(locale)}
                </p>
              )}
            </div>
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${metric.iconClass}`}
              aria-hidden="true"
            >
              {metric.icon}
            </div>
          </div>
          <p className="mt-4 text-sm leading-5 text-slate-500">{metric.detail}</p>
        </article>
      ))}
    </section>
  );
};
